/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react';
import { safeSetItem, safeGetItem, safeRemoveItem } from '@/lib/utils';

interface PlaylistQueueItem {
  id: string;
  name: string;
  spotify_playlist_id: string;
  track_count?: number;
}

interface SpotifyPlayerContextType {
  currentPlaylist: PlaylistQueueItem | null;
  isMinimized: boolean;
  setCurrentPlaylist: (playlist: PlaylistQueueItem | null, options?: { minimized?: boolean }) => void;
  minimize: () => void;
  maximize: () => void;
  close: () => void;
}

const SpotifyPlayerContext = createContext<SpotifyPlayerContextType | undefined>(undefined);

export const useSpotifyPlayerContext = () => {
  const context = useContext(SpotifyPlayerContext);
  if (!context) {
    throw new Error('useSpotifyPlayerContext must be used within SpotifyPlayerProvider');
  }
  return context;
};

interface SpotifyPlayerProviderProps {
  children: ReactNode;
}

const STORAGE_KEY_CURRENT = 'spotivibly_player_current';

function isValidPlaylistQueueItem(parsed: unknown): parsed is PlaylistQueueItem {
  if (!parsed || typeof parsed !== "object") return false;
  const p = parsed as Record<string, unknown>;
  return (
    typeof p.id === "string" &&
    typeof p.name === "string" &&
    typeof p.spotify_playlist_id === "string" &&
    (p.track_count === undefined || typeof p.track_count === "number")
  );
}

export const SpotifyPlayerProvider = ({ children }: SpotifyPlayerProviderProps) => {
  // Initialize state from localStorage with error handling
  const [currentPlaylist, setCurrentPlaylist] = useState<PlaylistQueueItem | null>(() => {
    try {
      const saved = safeGetItem(STORAGE_KEY_CURRENT);
      if (!saved) return null;
      const parsed: unknown = JSON.parse(saved);
      // localStorage is user-mutable; a stale or hand-edited entry would
      // otherwise flow into Spotify embed src as a string and break the
      // player. Validate shape before trusting it.
      return isValidPlaylistQueueItem(parsed) ? parsed : null;
    } catch (_error) {
      // silent — non-critical
      return null;
    }
  });
  
  const [isMinimized, setIsMinimized] = useState(false);

  // Persist current playlist to localStorage
  useEffect(() => {
    if (currentPlaylist) {
      safeSetItem(STORAGE_KEY_CURRENT, JSON.stringify(currentPlaylist));
    } else {
      try { safeRemoveItem(STORAGE_KEY_CURRENT); } catch { /* localStorage unavailable */ }
    }
  }, [currentPlaylist]);

  // Clean up old queue/history localStorage keys (migration)
  useEffect(() => {
    try {
      safeRemoveItem('spotivibly_player_queue');
      safeRemoveItem('spotivibly_player_history');
    } catch { /* localStorage unavailable */ }
  }, []);

  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximize = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const close = useCallback(() => {
    setCurrentPlaylist(null);
    setIsMinimized(false);
  }, []);

  const handleSetCurrentPlaylist = useCallback((playlist: PlaylistQueueItem | null, options?: { minimized?: boolean }) => {
    setCurrentPlaylist(playlist);
    setIsMinimized(options?.minimized ?? false);
  }, []);

  const value = useMemo(() => ({
    currentPlaylist,
    isMinimized,
    setCurrentPlaylist: handleSetCurrentPlaylist,
    minimize,
    maximize,
    close,
  }), [currentPlaylist, isMinimized, handleSetCurrentPlaylist, minimize, maximize, close]);

  return (
    <SpotifyPlayerContext.Provider value={value}>
      {children}
    </SpotifyPlayerContext.Provider>
  );
};
